import { Select } from '@purpurds/purpur';
import {
  TeliaButton,
  TeliaDivider,
  TeliaFieldset,
  TeliaForm,
  TeliaTextarea,
  TeliaTextInput,
} from '@teliads/components/react';
import { FormEvent, useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { useTranslation } from 'react-i18next';

import BaseModal from '../base-modal/BaseModal';
import CheckboxValidationWrapper from '../checkbox-validation-wrapper/CheckboxValidationWrapper';
import LoadingAnimation from '../loading-screen/LoadingAnimation';

import { VALID_EMAIL_PATTERN, VALID_PHONE_NUMBER_PATTERN } from './validations';

import Paragraph from 'components/voca/paragraph/Paragraph';
import { submitContactForm } from 'services/contact-us.service';
import { CheckboxField } from 'utils/models';

import './ContactUsModal.scss';

enum FIELDSET {
  CONSENT = 'consent',
}

type Props = {
  isOpen: boolean;
  close: () => void;
  nextStep: () => void;
};

const getCheckedModelValues = (fields: CheckboxField[]) => {
  return fields.reduce((acc: string[], field) => {
    if (field.isChecked) {
      acc.push(field.model);
    }

    return acc;
  }, []);
};

export default function ContactUsModal({ isOpen, close, nextStep }: Props) {
  const recaptchaRef = useRef<ReCAPTCHA>();
  const reCaptchaPublicKey = process.env.REACT_APP_GOOGLE_RE_CAPTCHA_PUBLIC_KEY_CONTACT_FORM;
  const { t } = useTranslation();

  const initialFormData = {
    fieldOfInterest: '',
    name: '',
    lastName: '',
    companyName: '',
    country: '',
    phone: '',
    email: '',
    message: '',
    [FIELDSET.CONSENT]: [
      {
        name: 'consent',
        value: t('contact_us_modal.consent-label'),
        model: 'conset',
        isChecked: false,
      },
    ],
  };
  const [formData, setFormData] = useState(initialFormData);
  const [isSubmitClicked, setIsSubmitClicked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  function setInputFieldData(e: FormEvent) {
    const target = e.target as HTMLTeliaTextInputElement;
    setFormData((prevState) => ({
      ...prevState,
      [target.name]: target.value,
    }));
  }

  const setCheckboxFieldData = (fieldset: FIELDSET, index: number, isChecked: boolean) => {
    const fieldsetData = formData[fieldset];
    fieldsetData.splice(index, 1, { ...fieldsetData[index], isChecked });

    setFormData((prevState) => {
      return {
        ...prevState,
        fieldset: fieldsetData,
      };
    });
  };

  const isCheckboxesInValid = getCheckedModelValues(formData[FIELDSET.CONSENT]).length === 0;

  const submitForm = async (e: FormEvent) => {
    e.preventDefault();
    const { name, lastName, fieldOfInterest, companyName, country, email, message, phone } =
      formData;

    if (isCheckboxesInValid) {
      return;
    }

    try {
      const token = await recaptchaRef.current?.executeAsync();
      setIsLoading(true);
      if (token) {
        const response = await submitContactForm({
          name,
          lastName,
          fieldOfInterest,
          companyName,
          country,
          email,
          message,
          phone,
          token,
        });
        if (response.status) {
          setIsLoading(false);
        }
        setFormData(initialFormData);
        nextStep();
        setIsSubmitClicked(false);
      }
    } catch (e) {
      console.error(e);
    }
  };

  async function closeModal() {
    setFormData(initialFormData);

    setIsSubmitClicked(false);

    close();
  }

  function onSubmitClick() {
    setIsSubmitClicked(true);
  }

  const fieldOfInterest = Object.keys(
    t('contact_us_modal.field_of_interest', { returnObjects: true })
  ).map((key) => ({
    label: t(`contact_us_modal.field_of_interest.${key}`),
    value: t(`contact_us_modal.field_of_interest.${key}`),
  }));

  const formValues = {
    defaultvalue: {
      label: '',
      value: 'default',
    },
    fieldOfInterest: fieldOfInterest,
  };

  const placeholder = { label: '', value: '' };

  return (
    <BaseModal
      variant="center-large"
      header={'Contact us'}
      onClose={closeModal}
      isOpen={isOpen}
      focusFirstInteractive
      isRenderedOnOpen
    >
      <div className="contact_us_modal">
        <TeliaForm name="contact_us_modal" onSubmit={submitForm}>
          <div className={`contact_us_modal__loading-animation${isLoading ? '--active' : ''} `}>
            <LoadingAnimation />
          </div>
          <Paragraph>{t('contact_us_modal.preamble')}</Paragraph>
          <div className="contact-us-form">
            <div className="contact-us-form__block">
              <div className="contact-us-form__custom-select">
                <Select
                  label={t('contact_us_modal.foi')}
                  name="fieldOfInterest"
                  //@ts-ignore
                  placeholder={placeholder}
                  options={formValues.fieldOfInterest}
                  value={formData.fieldOfInterest}
                  onInput={(e: FormEvent) => setInputFieldData(e)}
                  required
                />
              </div>

              <TeliaTextInput
                inputmode="text"
                type="text"
                label={t('contact_us_modal.name')}
                name="name"
                value={formData.name}
                onInput={(e: FormEvent) => setInputFieldData(e)}
                maxlength={100}
                required
                requiredErrorMessage={t('error_messages.required_field')}
              />

              <TeliaTextInput
                inputmode="text"
                type="text"
                label={t('contact_us_modal.last_name')}
                name="lastName"
                value={formData.lastName}
                onInput={(e: FormEvent) => setInputFieldData(e)}
                maxlength={100}
                required
                requiredErrorMessage={t('error_messages.required_field')}
              />

              <TeliaTextInput
                inputmode="text"
                type="text"
                label={t('contact_us_modal.company')}
                name="companyName"
                value={formData.companyName}
                onInput={(e: FormEvent) => setInputFieldData(e)}
                maxlength={100}
                required
                requiredErrorMessage={t('error_messages.required_field')}
              />

              <TeliaTextarea
                label={t('contact_us_modal.message')}
                minlength={0}
                maxlength={1000}
                rows={7}
                name="message"
                className="contact-us-form__textarea"
                onInput={(e: FormEvent) => setInputFieldData(e)}
              />
            </div>

            <div className="contact-us-form__block">
              <TeliaTextInput
                inputmode="text"
                type="text"
                label={t('contact_us_modal.country')}
                name="country"
                value={formData.country}
                onInput={(e: FormEvent) => setInputFieldData(e)}
                maxlength={100}
                required
                requiredErrorMessage={t('error_messages.required_field')}
              />

              <TeliaTextInput
                inputmode="email"
                type="email"
                label={t('contact_us_modal.email')}
                name="email"
                value={formData.email}
                onInput={(e: FormEvent) => setInputFieldData(e)}
                required
                placeholder={'Please provide business email address'}
                requiredErrorMessage={t('error_messages.required_field')}
                pattern={VALID_EMAIL_PATTERN}
                patternErrorMessage={t('error_messages.please_enter_correct_email')}
                maxlength={100}
              />
              <TeliaTextInput
                type="tel"
                label={t('contact_us_modal.phone')}
                name="phone"
                value={formData.phone}
                required
                requiredErrorMessage={t('error_messages.required_field')}
                pattern={VALID_PHONE_NUMBER_PATTERN}
                patternErrorMessage={t('error_messages.incorrect_phone_number')}
                onInput={(e: FormEvent) => setInputFieldData(e)}
              />

              <ReCAPTCHA
                /* @ts-ignore */
                ref={recaptchaRef}
                sitekey={reCaptchaPublicKey!}
                size="invisible"
              />

              <TeliaFieldset legend={t('Consent')} required>
                <CheckboxValidationWrapper
                  fields={formData[FIELDSET.CONSENT]}
                  onChecked={(index: number, isChecked: boolean) =>
                    setCheckboxFieldData(FIELDSET.CONSENT, index, isChecked)
                  }
                  errorMessage={t('error_messages.required_field')}
                  isSubmitClicked={isSubmitClicked}
                  singleCheckbox
                />
              </TeliaFieldset>

              <Paragraph>
                {t('contact_us_modal.info')}
                <a href="https://www.teliacompany.com/en/about-the-company/privacy/">
                  {t('contact_us_modal.info_link_text')}
                </a>
              </Paragraph>

              <TeliaDivider />

              <div className="contact-us-form__buttons-wrapper">
                <TeliaButton
                  variant="primary"
                  allyLabel="Send form data and close modal"
                  type="submit"
                  onClick={onSubmitClick}
                >
                  {t('actions.confirm')}
                </TeliaButton>

                <TeliaButton
                  variant="tertiary-purple"
                  allyLabel="Close modal"
                  type="button"
                  onClick={closeModal}
                >
                  {t('actions.cancel')}
                </TeliaButton>
              </div>
            </div>
          </div>
        </TeliaForm>
      </div>
    </BaseModal>
  );
}
